/**
 * useCamera — shared type definitions
 *
 * All types exported from the useCamera barrel file are defined here.
 */

// ==================== CAMERA TYPES ====================

export type CameraFacingMode = 'user' | 'environment';
export type CameraFacingBucket = CameraFacingMode | 'unknown';
export type CameraFillLightMode = 'off' | 'torch' | 'flash';
export type CameraFocusMode = 'manual' | 'single-shot' | 'continuous';

/**
 * Crop rectangle in relative coordinates (0–1) for digital cropping of captured photos.
 * The region { x, y, width, height } is expressed as fractions of the source image dimensions.
 */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CameraDeviceOption {
  deviceId: string;
  label: string;
  facing: CameraFacingBucket;
}

export interface CameraPointOfInterest {
  x: number;
  y: number;
}

export type ExtendedMediaTrackCapabilities = MediaTrackCapabilities & {
  zoom?: MediaSettingsRange;
  torch?: boolean;
  focusMode?: string[];
  pointsOfInterest?: unknown;
};

export interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
  zoom?: number;
  torch?: boolean;
  focusMode?: CameraFocusMode;
  pointsOfInterest?: CameraPointOfInterest[];
}

export type ExtendedMediaTrackConstraints = MediaTrackConstraints & {
  advanced?: ExtendedMediaTrackConstraintSet[];
};

export type ExtendedMediaTrackSettings = MediaTrackSettings & {
  torch?: boolean;
  zoom?: number;
};

export interface ImageCapturePhotoCapabilities {
  fillLightMode?: string[];
}

export interface ImageCaptureLike {
  getPhotoCapabilities?: () => Promise<ImageCapturePhotoCapabilities>;
  setOptions: (options: { fillLightMode?: CameraFillLightMode }) => Promise<void>;
  takePhoto: (options?: { fillLightMode?: CameraFillLightMode }) => Promise<Blob>;
}

export type ImageCaptureConstructor = new (track: MediaStreamTrack) => ImageCaptureLike;

export type WindowWithImageCapture = Window & typeof globalThis & {
  ImageCapture?: ImageCaptureConstructor;
};

// ==================== HOOK CONFIG & RETURN ====================

export interface UseCameraConfig {
  /** Called when a photo is captured with base64 data */
  onCapture?: (base64: string) => void;
  /** Called when camera is closed */
  onClose?: () => void;
  /** When true, onCapture is deferred until the user confirms the photo via submitPhoto (for review mode) */
  reviewCapture?: boolean;
  /** Initial camera facing mode (default: 'environment' for back camera) */
  initialFacingMode?: CameraFacingMode;
  /** JPEG quality 0-1 (default: 0.85) */
  quality?: number;
  /** Preferred video width (default: 1920) */
  preferredWidth?: number;
  /** Preferred video height (default: 1080) */
  preferredHeight?: number;
  /** Ideal stream width in px for getUserMedia (default: 1280).
   *  Raise for dense-barcode scanning (e.g. PDF417) — more pixels per
   *  barcode module materially improves decode reliability. Note that
   *  higher resolutions can make some phones select a narrower-FOV lens. */
  videoWidth?: number;
  /** Ideal stream height in px for getUserMedia (default: 720). */
  videoHeight?: number;
  /** Automatically enable torch when supported on the back camera */
  enableTorchByDefault?: boolean;
  /** Legacy compatibility flag. The browser/in-app JS camera is now always used. */
  preferNativeCameraOnCapacitor?: boolean;
  /**
   * Post-capture compression: scales the captured image so neither dimension
   * exceeds `maxDim` and re-encodes at `quality`.
   * When omitted, the raw capture is returned as-is.
   */
  compressOutput?: {
    maxDim?: number;
    quality?: number;
  };
}

export interface UseCameraReturn {
  /** Whether camera modal is open */
  isOpen: boolean;
  /** Open the camera */
  open: () => void;
  /** Close the camera */
  close: () => void;
  /** Ref to attach to video element */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Callback ref for wiring the active video element into the hook */
  attachVideoElement: (node: HTMLVideoElement | null) => void;
  /** Current media stream (null if not started) */
  stream: MediaStream | null;
  /** Error message if camera failed */
  error: string | null;
  /** Current facing mode */
  facingMode: CameraFacingMode;
  /** Switch between front and back camera */
  switchCamera: () => Promise<void>;
  /** Whether the device appears to expose more than one camera */
  canSwitchCamera: boolean;
  /** Number of enumerated video inputs available */
  availableCameraCount: number;
  /** Capture current frame as base64 JPEG (optionally cropped to `cropRect`) */
  capture: (cropRect?: CropRect) => Promise<string | null>;
  /** Whether camera is currently initializing */
  isLoading: boolean;
  /** Start the camera (used by CameraModal internally) */
  startCamera: (facing?: CameraFacingMode) => Promise<void>;
  /** Whether torch/flash is currently on */
  torchEnabled: boolean;
  /** Whether the current camera track supports torch */
  torchSupported: boolean;
  /** Whether torch support is currently being probed or recovered */
  isTorchChecking: boolean;
  /** Toggle torch/flash on or off */
  toggleTorch: () => Promise<void>;
  /** Set torch/flash hardware state directly (does not persist user preference) */
  setTorch: (enabled: boolean) => Promise<boolean>;
  /** Current ambient brightness level (0–255, null if not sampling) */
  ambientBrightness: number | null;
  /** Current zoom level (1 = no zoom) */
  zoomLevel: number;
  /** Min zoom supported */
  zoomMin: number;
  /** Max zoom supported */
  zoomMax: number;
  /** Whether zoom is supported on this track */
  zoomSupported: boolean;
  /** Whether zoom is digital (CSS transform) rather than native (MediaTrackConstraints) */
  isDigitalZoom: boolean;
  /** Set zoom to a specific level */
  setZoom: (level: number) => Promise<void>;
  /** Trigger tap-to-focus at a point (x/y as 0-1 fractions of the video viewport).
   *  When `lock` is true, the selected focus is held (manual mode) instead of
   *  reverting to continuous autofocus after a short delay. */
  tapToFocus: (x: number, y: number, lock?: boolean) => Promise<void>;
  /** Whether continuous autofocus is active */
  autofocusActive: boolean;
  /** Toggle between continuous autofocus (AF) and manual focus (MF) */
  toggleFocusMode: () => Promise<void>;
  /** The ref for the stream (used internally for gesture handlers) */
  streamRef: React.MutableRefObject<MediaStream | null>;
  /** Submit a photo to the onCapture callback (used when reviewCapture defers onCapture) */
  submitPhoto: (base64: string) => void;
}

// ==================== CAMERA MODAL TYPES ====================

export interface CameraModalProps {
  /** useCamera hook return value */
  camera: UseCameraReturn;
  /** Title shown in header (default: "Take Photo") */
  title?: string;
  /** Optional framing guide overlay for document capture */
  guide?: {
    mode?: 'auto' | 'receipt' | 'invoice' | 'continuous' | 'vehicle';
    title?: string;
    subtitle?: string;
  };
  /** Vehicle profile for the 'vehicle' guide mode.
   *  Determines the silhouette shape shown as a framing guide.
   *  - 'front': panel van front outline (tall, boxy)
   *  - 'rear': panel van rear outline (flat back, barn doors)
   *  - 'side': panel van side profile (high roof, long box body, front faces left)
   *  - 'left_side': side profile for the vehicle's left side (front faces left)
   *  - 'right_side': mirrored side profile for the vehicle's right side (front faces right)
   *  - 'interior': steering wheel outline
   *  - 'dashboard': instrument cluster outline
   *  Defaults to 'front'. */
  vehicleProfile?: 'front' | 'rear' | 'side' | 'left_side' | 'right_side' | 'interior' | 'dashboard';
  /** Preferred device orientation for this capture.
   *  - 'landscape': encourages the user to hold the phone in landscape.
   *    On Capacitor/mobile, attempts to lock the screen to landscape while
   *    the camera is open. On web, shows a "rotate to landscape" hint when
   *    the phone is in portrait. The guide frame uses landscape dimensions
   *    regardless of current orientation. Capture is NOT blocked in portrait.
   *  - 'portrait': encourages portrait (default behavior, no lock).
   *  - undefined: no preference (default). */
  preferredOrientation?: 'portrait' | 'landscape';
  /** When true, digitally crops the captured photo to the guide frame region */
  cropToGuide?: boolean;
  /** Custom capture button component */
  captureButton?: React.ReactNode;
  /** Called after successful capture (for haptics, closing, etc.) */
  onAfterCapture?: () => void;
  /** Enable auto-capture when document edge is stable and confident (default: false) */
  autoCapture?: boolean;
  /** Confidence threshold for auto-capture (default: 0.65).
   *  Higher values require stronger contrast between the document and
   *  background, reducing false positives from random brightness gradients. */
  autoCaptureConfidence?: number;
  /** How long the edge must be stable before countdown starts, in ms (default: 2000) */
  autoCaptureStabilityMs?: number;
  /** Countdown duration in seconds (default: 3) */
  autoCaptureCountdownSec?: number;
  /** Show a review screen after capture with Use/Retake buttons (default: false) */
  reviewCapture?: boolean;
  /** Enable batch/multi-page capture — keeps camera open for successive captures (default: false) */
  batchCapture?: boolean;
  /** Max number of photos in batch mode (default: 10) */
  batchMaxPhotos?: number;
  /** Contextual quick-action shortcuts shown as pills above the shutter button */
  shortcuts?: CaptureShortcut[];
  /** 2×2 grid of capture mode buttons that replaces the shortcuts bar and shutter.
   *  When provided, the grid renders with up to 4 buttons in a square layout.
   *  The button with isCapture=true is placed bottom-right as the generic capture. */
  captureModeGrid?: CaptureModeButton[];
  /** Enable hardware volume button to capture (mobile only, default: true) */
  hardwareCapture?: boolean;
  /** Enable perspective correction in the review overlay (default: false) */
  perspectiveCorrection?: boolean;
  /** Enable dark mode capture enhancement: auto-torch, low-light warning, post-capture brightness boost (default: false) */
  darkModeEnhance?: boolean;
  /** Review overlay button layout:
   *  - 'default': Retake, Cancel, Flatten, Use Photo (original behavior)
   *  - 'captureAnotherDone': Cancel (=retake), Add Another, Use Photo (batch-aware flow).
   *    When multi-page is possible (partial till-slip detection, A4, or
   *    batch mode), shows all three buttons on one screen.
   *    When single-page is known (all corners detected, non-batch), shows
   *    Cancel + Use Photo (2 buttons).
   *  - 'doneOnly': Done only (no cancel, no add page). For flows where the
   *    photo is always single-page and retake is not needed (e.g. casual
   *    wage authorization photo).
   *  Defaults to 'default'. */
  reviewButtons?: 'default' | 'captureAnotherDone' | 'doneOnly';
  /** Enable auto-zoom for receipt/document framing (default: false).
   *  Can coexist with autoCapture — when both are enabled, auto-capture only
   *  fires after auto-zoom has settled on a stable framing (no active zoom
   *  animation and the zoom level is within tolerance of the target fill ratio).
   *  Any zoom adjustment or edge movement cancels the auto-capture countdown. */
  autoZoom?: boolean;
  /** Target fill ratio for auto-zoom (default: 0.75 = document fills 75% of frame) */
  autoZoomTargetFill?: number;
  /** Confidence threshold for auto-zoom to act on edge detection (default: 0.3) */
  autoZoomConfidence?: number;
  /** Read-only reference photos shown as a strip at the top of the camera viewfinder.
   *  Used when reopening the camera via "Add Page" so the user can see already-captured
   *  pages and overlap correctly with the last one. These are display-only (no remove buttons). */
  contextPhotos?: string[];
  /** Capture mode selector:
   *  - 'single' (default): one photo = one document, camera closes after capture
   *  - 'combine': multiple photos = one multi-page document (batchCapture behavior)
   *  - 'multiple': multiple photos = each becomes a separate document.
   *    onBatchComplete is called with all photos when user taps Done.
   *  When omitted, defaults to 'single' (or 'combine' if batchCapture is true). */
  captureMode?: 'single' | 'combine' | 'multiple';
  /** Called when captureMode='multiple' and the user taps Done.
   *  Receives an array of individually-captured photos, each to be a separate document. */
  onBatchComplete?: (photos: string[]) => void;
  /** Expense categorization text shown as a black bar above the review buttons.
   *  Provides a final recap of what the user is submitting (e.g. "Job linked - JB1234 / City Properties (Pty) Ltd").
   *  Supports multi-line via \n. Only shown on the review screen. */
  expenseContext?: string;
  /** Document profile hint — tells the camera what kind of document to expect.
   *  - 'till-slip': long narrow receipt (fuel, supplier, general). Auto-zoom uses
   *    a lower fill target and caps zoom to avoid over-zooming long slips.
   *    Multi-page prompt is skipped (till slips are always single-page).
   *  - 'a4': A4/Letter document (invoices, statements). Standard auto-zoom.
   *  - 'auto' (default): no hint, auto-detect from ML edge aspect ratio. */
  documentProfile?: 'till-slip' | 'a4' | 'auto';
  /** Enable dynamic edge detection — the bounding-box overlay that drives
   *  auto-capture and auto-zoom (default: true). Set false to disable the
   *  detectors entirely; the static guide frame is shown instead. Has no
   *  effect on modes that never run detection (e.g. 'manual' guide). */
  edgeDetection?: boolean;
  /** Require the detected document to be readable before capture (default:
   *  false). When a document is detected but its text isn't resolvable —
   *  the slip is too far away, blurry, or lacks text-scale detail — the
   *  bounding box turns orange with "move closer" guidance, auto-capture
   *  never fires, and the first manual capture tap is blocked with a
   *  warning. A second tap within 4s forces the capture so a mis-detection
   *  can never lock the user out. Captures with no detected document are
   *  unaffected. Only applies in 'auto' guide mode. */
  requireReadableDocument?: boolean;
}

export interface CaptureShortcut {
  /** Unique key for the shortcut */
  key: string;
  /** Label shown on the pill button */
  label: string;
  /** Optional emoji or short icon identifier */
  icon?: string;
  /** Callback when the shortcut is tapped */
  onClick: () => void;
  /** Whether this shortcut is currently active/selected */
  active?: boolean;
}

export interface CaptureModeButton {
  /** Unique key for the button */
  key: string;
  /** Label shown on the button */
  label: string;
  /** Optional emoji or short icon identifier */
  icon?: string;
  /** Callback when the button is tapped (e.g. to set entry type) before capture */
  onClick: () => void;
  /** Whether this is the primary capture button (rendered bottom-right) */
  isCapture?: boolean;
  /** When set, this button controls a camera function instead of triggering capture.
   *  The CameraModal handles the toggle internally and displays live state. */
  cameraAction?: 'flash' | 'focusMode' | 'autoCapture';
  /** When true, clicking this button does not trigger capture (e.g. configuration prompts) */
  skipCapture?: boolean;
}
